/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/EventThemeApplier.tsx
 *
 * Rôle :
 *   Applique À CHAUD la palette de l'événement actif (fondation
 *   multi-événement). Monté UNE fois dans `AppLayout`.
 *
 *   Mécanique zéro-régression : les `var(--tne-color-*, #hexTNE)` ne
 *   sont JAMAIS définies dans le CSS compilé (seul le fallback hex =
 *   palette TNE Vosges s'applique). Ici :
 *     - si l'événement a un `branding.palette` valide → on pose
 *       `--tne-color-<clé>` sur `:root` ⇒ tout le CSS bascule à chaud.
 *     - sinon (TNE par défaut / offline / V25 absente) → on ne pose
 *       RIEN ⇒ les fallbacks hex TNE Vosges restent ⇒ identique à
 *       aujourd'hui.
 *
 *   Ne rend aucun DOM (composant d'effet). Nettoie les variables au
 *   démontage / changement d'événement.
 *
 * Voir : hooks/useEventConfig.ts, styles/tokens.css,
 *        app/docs/MULTI-EVENT-ARCHITECTURE.md.
 *
 * @author Mika
 * @since  2026-05-16 (fondation multi-événement)
 */

import { useEffect } from 'react';
import { useEventConfig } from '@/hooks/useEventConfig';

/** Clés de palette autorisées → nom de variable CSS appliquée. */
const PALETTE_TO_CSSVAR: Readonly<Record<string, string>> = {
  primary: '--tne-color-primary',
  accent: '--tne-color-accent',
  orange: '--tne-color-orange',
  cream: '--tne-color-cream',
  'text-strong': '--tne-color-text-strong',
  'on-primary': '--tne-color-on-primary',
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function EventThemeApplier(): null {
  const { event } = useEventConfig();
  const palette = event.branding?.palette ?? null;

  useEffect(() => {
    if (!palette || typeof document === 'undefined') {
      return; // pas de branding → on garde le thème compilé (TNE)
    }
    const root = document.documentElement;
    const applied: string[] = [];

    for (const [key, cssVar] of Object.entries(PALETTE_TO_CSSVAR)) {
      const value = (palette as Record<string, string | undefined>)[key];
      if (typeof value === 'string' && HEX_RE.test(value)) {
        root.style.setProperty(cssVar, value);
        applied.push(cssVar);
      }
    }

    return () => {
      // Restaure le thème compilé si l'événement change / démontage.
      for (const cssVar of applied) {
        root.style.removeProperty(cssVar);
      }
    };
  }, [palette]);

  return null;
}

export default EventThemeApplier;
